import React from 'react';
import { AiAgentAvatar, isAiAgentId } from '../atoms/ai-agent-avatar';
import styled, { keyframes } from 'styled-components';
import { Text, TextSize } from '../atoms/text';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled components - Synthora-inspired design
const UserContainer = styled.div<{ $isSelected: boolean; $isOnline?: boolean }>`
    /* Each conversation is its own white card (website advisor-card style) */
    background: ${(props) => (props.$isSelected ? '#f3f6ff' : '#ffffff')};
    display: flex;
    padding: 14px 16px;
    margin: 8px 12px;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    border-radius: 12px;
    border: 1px solid ${(props) => props.$isSelected ? '#9fb6f5' : '#e0e8f5'};
    box-shadow: ${(props) => props.$isSelected
      ? '0 0 0 4px rgba(88, 126, 244, 0.08)'
      : '0 3px 12px rgba(37, 65, 122, 0.04)'};
    animation: ${fadeIn} 0.3s ease-out;
    
    /* Left accent bar for selected or online */
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 60%;
      width: 3px;
      background: ${(props) => props.$isSelected 
        ? 'linear-gradient(180deg, #335cf4 0%, #885de8 100%)' 
        : props.$isOnline ? '#12a571' : 'transparent'};
      border-radius: 2px;
      transition: all 0.2s ease;
    }
    
    &:hover {
      background: ${(props) => props.$isSelected ? '#eef3ff' : '#fbfcff'};
      border-color: ${(props) => props.$isSelected ? '#8ea9f5' : '#bccdf0'};
      box-shadow: ${(props) => props.$isSelected
        ? '0 0 0 4px rgba(88, 126, 244, 0.1)'
        : '0 8px 22px rgba(52, 77, 151, 0.08)'};
    }
    
    &:active {
      transform: scale(0.98);
    }
    
    @media (max-width: 768px) {
      padding: 12px 14px;
      margin: 3px 10px;
      border-radius: 10px;
    }
`;

const UserInfoSection = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    color: #192947;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    
    @media (max-width: 768px) {
        gap: 10px;
    }
`;

const AvatarWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`;

const UserAvatar = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: none;
    object-fit: cover;
    background: #edf2ff;
    transition: all 0.2s ease;

    ${UserContainer}:hover & {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        width: 38px;
        height: 38px;
        border-radius: 8px;
    }
`;

const UserDetails = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    gap: 4px;
    min-width: 0;
    flex: 1;
`;

const UserName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    p {
        /* the ellipsis must be on the element that holds the text, or long names run under the ⋮ button */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 500;
        font-size: 14px;
        letter-spacing: 0.1px;
        color: #192947;
    }
`;

const UserMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const UserDate = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    .date-icon {
        font-size: 10px;
        opacity: 0.6;
    }
`;

const UserRightSection = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    
    @media (max-width: 768px) {
        opacity: 1;
    }
`;

const StatusContainer = styled.div`
    display: none;
`;

const MoreButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.15s ease;
    
    &:hover {
        opacity: 0.9;
    }
    
    .dots {
        display: flex;
        flex-direction: column;
        gap: 3px;
        
        span {
            width: 4px;
            height: 4px;
            background: #65748d;
            border-radius: 50%;
        }
    }
`;

const LineContainer = styled.div`
    display: none;
`;

// Types
interface UserListItemProps {
    name: string;
    img?: string;
    conversationStartTime: string;
    isOnline: boolean;
    isSelected: boolean;
    threadId: string;
    expertId: string;
    onClick: () => void;
    onMoreOptionsClick: (event: React.MouseEvent<Element>, threadId: string, expertId: string) => void;
}

export const UserListItem: React.FC<UserListItemProps> = ({
    name,
    img,
    conversationStartTime,
    isOnline,
    isSelected,
    threadId,
    expertId,
    onClick,
    onMoreOptionsClick
}) => {
    const formattedDate = new Date(conversationStartTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });

    return (
        <div onClick={onClick} className="ai-hover">
            <UserContainer $isSelected={isSelected} $isOnline={isOnline}>
                <UserInfoSection>
                    <AvatarWrapper>
                        {isAiAgentId(expertId) ? (
                            <AiAgentAvatar size={40} label={`${name} (AI agent)`} />
                        ) : (
                            <UserAvatar
                                src={img || "https://icons.veryicon.com/png/o/internet--web/prejudice/user-128.png"}
                                alt={name}
                            />
                        )}
                    </AvatarWrapper>
                    <UserDetails>
                        <UserName>
                            <Text
                                size={TextSize.SM}
                                color="#192947"
                            >
                                {name}
                            </Text>
                        </UserName>
                        <UserMeta>
                            <UserDate>
                                <Text
                                    size={TextSize.XS}
                                    color="#8f9db3"
                                >
                                    {formattedDate}
                                </Text>
                            </UserDate>
                        </UserMeta>
                    </UserDetails>
                </UserInfoSection>
                <UserRightSection>
                    <MoreButton 
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoreOptionsClick(e, threadId, expertId);
                        }}
                    >
                        <div className="dots">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </MoreButton>
                </UserRightSection>
            </UserContainer>
        </div>
    );
};

export default UserListItem;
